import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../contexts/UserAuthContext';
import { getCountriesForForm, getCountryFlag, getCountryName } from '../utils/countries';
import config from '../config';

const API_URL = config.API_BASE_URL;

function ProfileScreen() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, updateProfile, logout } = useUserAuth();

  const [nombre, setNombre] = useState('');
  const [pais, setPais] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  const [prayers, setPrayers] = useState([]);
  const [prayerStats, setPrayerStats] = useState({ total_prayers: 0, total_received: 0 });
  const [loadingPrayers, setLoadingPrayers] = useState(true);

  const [prefs, setPrefs] = useState({ notify_on_prayer: true, weekly_summary: true });
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [prefsMsg, setPrefsMsg] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setNombre(user.nombre || '');
      setPais(user.pais || '');
    }
  }, [user]);

  const fetchPrayers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/my-prayers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPrayers(data.prayers);
        setPrayerStats(data.stats);
      }
    } catch (err) {
      console.error('Error cargando pedidos:', err);
    } finally {
      setLoadingPrayers(false);
    }
  }, [token]);

  const fetchPreferences = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/my-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPrefs(data.preferences);
      }
    } catch (err) {
      console.error('Error cargando preferencias:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchPrayers();
    fetchPreferences();
  }, [fetchPrayers, fetchPreferences]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!nombre || nombre.trim().length < 2) {
      setProfileMsg('El nombre debe tener al menos 2 caracteres');
      return;
    }
    setIsSavingProfile(true);
    setProfileMsg('');

    const result = await updateProfile({ nombre: nombre.trim(), pais });
    if (result.success) {
      setProfileMsg('Perfil actualizado');
      setTimeout(() => setProfileMsg(''), 3000);
    } else {
      setProfileMsg(result.error || 'Error al guardar');
    }
    setIsSavingProfile(false);
  };

  const handleTogglePref = async (key) => {
    const newPrefs = { ...prefs, [key]: !prefs[key] };
    setPrefs(newPrefs);
    setIsSavingPrefs(true);
    setPrefsMsg('');

    try {
      const res = await fetch(`${API_URL}/api/auth/my-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPrefs)
      });
      const data = await res.json();
      if (data.success) {
        setPrefsMsg('Preferencias guardadas');
        setTimeout(() => setPrefsMsg(''), 2000);
      } else {
        setPrefs(prefs);
        setPrefsMsg('Error al guardar');
      }
    } catch {
      setPrefs(prefs);
      setPrefsMsg('Error de conexión');
    }
    setIsSavingPrefs(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const countries = getCountriesForForm();

  if (!isAuthenticated || !user) return null;

  return (
    <div className="profile-screen">
      <div className="profile-header-bar">
        <button className="profile-back-btn" onClick={() => navigate('/')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Volver
        </button>
        <h1 className="profile-page-title">Mi perfil</h1>
        <div style={{ width: 80 }}></div>
      </div>

      <div className="profile-content">
        {/* Avatar + identity */}
        <div className="profile-identity">
          <div className="profile-avatar-large">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" referrerPolicy="no-referrer" />
            ) : (
              <span>{(user.nombre || user.email)[0].toUpperCase()}</span>
            )}
          </div>
          <div className="profile-identity-info">
            <h2>{user.nombre || 'Sin nombre'}</h2>
            <p>{user.email}</p>
          </div>
        </div>

        {/* Stats cards */}
        <div className="profile-stats-row">
          <div className="profile-stat-card">
            <span className="profile-stat-number">{prayerStats.total_prayers}</span>
            <span className="profile-stat-label">Pedidos enviados</span>
          </div>
          <div className="profile-stat-card">
            <span className="profile-stat-number">{prayerStats.total_received}</span>
            <span className="profile-stat-label">Oraciones recibidas</span>
          </div>
        </div>

        {/* Personal data section */}
        <div className="profile-section">
          <h3 className="profile-section-title">Datos personales</h3>
          <form onSubmit={handleSaveProfile} className="profile-form">
            <div className="profile-field">
              <label htmlFor="profile-email">Email</label>
              <input
                id="profile-email"
                type="email"
                value={user.email}
                disabled
                className="profile-input disabled"
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-nombre">Nombre</label>
              <input
                id="profile-nombre"
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="profile-input"
                placeholder="Tu nombre"
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-pais">País</label>
              <select
                id="profile-pais"
                value={pais}
                onChange={(e) => setPais(e.target.value)}
                className="profile-input"
              >
                <option value="">Seleccionar país</option>
                {countries.map(c => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </div>
            <div className="profile-form-actions">
              <button
                type="submit"
                className="profile-save-btn"
                disabled={isSavingProfile}
              >
                {isSavingProfile ? 'Guardando...' : 'Guardar cambios'}
              </button>
              {profileMsg && (
                <span className={`profile-msg ${profileMsg.includes('Error') ? 'error' : 'success'}`}>
                  {profileMsg}
                </span>
              )}
            </div>
          </form>
        </div>

        {/* Prayer requests section */}
        <div className="profile-section">
          <h3 className="profile-section-title">Mis pedidos de oración</h3>
          {loadingPrayers ? (
            <div className="profile-loading">
              <span className="load-more-spinner"></span> Cargando...
            </div>
          ) : prayers.length === 0 ? (
            <div className="profile-empty">
              <p>Aún no has enviado pedidos de oración</p>
              <button className="profile-cta-btn" onClick={() => navigate('/')}>
                Enviar mi primer pedido
              </button>
            </div>
          ) : (
            <div className="profile-prayers-list">
              {prayers.map((prayer) => (
                <div key={prayer.id} className="profile-prayer-card">
                  <div className="profile-prayer-top">
                    <span className="profile-prayer-category">{prayer.categoria || 'General'}</span>
                    <span className="profile-prayer-date">{formatDate(prayer.fecha)}</span>
                  </div>
                  <p className="profile-prayer-text">{prayer.comentario}</p>
                  <div className="profile-prayer-bottom">
                    <span className="profile-prayer-count">
                      {prayer.oraciones || 0} oraciones
                    </span>
                    {prayer.pais && (
                      <span className="profile-prayer-country">
                        {getCountryFlag(prayer.pais)} {getCountryName(prayer.pais)}
                      </span>
                    )}
                    {!prayer.visible && (
                      <span className="profile-prayer-private">Privado</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notification preferences section */}
        <div className="profile-section">
          <h3 className="profile-section-title">Notificaciones</h3>
          <div className="profile-prefs">
            <div className="profile-pref-row">
              <div className="profile-pref-info">
                <span className="profile-pref-title">Alguien ora por mi pedido</span>
                <span className="profile-pref-desc">Recibir un email cuando alguien ore por tu pedido</span>
              </div>
              <button
                className={`profile-toggle ${prefs.notify_on_prayer ? 'active' : ''}`}
                onClick={() => handleTogglePref('notify_on_prayer')}
                disabled={isSavingPrefs}
                aria-label="Notificar oraciones"
              >
                <span className="profile-toggle-knob"></span>
              </button>
            </div>
            <div className="profile-pref-row">
              <div className="profile-pref-info">
                <span className="profile-pref-title">Resumen semanal</span>
                <span className="profile-pref-desc">Recibir un resumen semanal de oraciones por tus pedidos</span>
              </div>
              <button
                className={`profile-toggle ${prefs.weekly_summary ? 'active' : ''}`}
                onClick={() => handleTogglePref('weekly_summary')}
                disabled={isSavingPrefs}
                aria-label="Resumen semanal"
              >
                <span className="profile-toggle-knob"></span>
              </button>
            </div>
            {prefsMsg && (
              <span className={`profile-msg ${prefsMsg.includes('Error') ? 'error' : 'success'}`}>
                {prefsMsg}
              </span>
            )}
          </div>
        </div>

        {/* Logout */}
        <div className="profile-section profile-logout-section">
          <button className="profile-logout-btn" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileScreen;
